import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import useUserAccount from '../../hooks/userAccountHook';
import useModal from '../../hooks/modalHook';
import ActionConfirmation from '../elements/ActionConfirmation';
import { 
  SidebarHeader, 
  CompanyInfo, 
  UserInfo, 
  AddPolicyButton, 
  Navigation, 
  SidebarFooter, 
  useSidebarState 
} from './index';

interface SidebarContentProps {
  onMenuClick?: () => void;
  onMenuItemClicked?: () => void;
  showSidebar?: boolean;
}

function SidebarContent({ onMenuClick, onMenuItemClicked, showSidebar }: SidebarContentProps) {
  const { signOut, user } = useUserAccount();
  const dispatch = useDispatch();
  const { ModalComponent, closeModal, updateModalStates } = useModal();
  
  const {
    isSidebarExpanded,
    expandedNavItems,
    expandedCategories,
    toggleCategory,
    toggleNavItem,
    toggleSidebarExpansion
  } = useSidebarState();

  const menuClickHandler = onMenuClick || onMenuItemClicked || (() => {});

  const logout = useCallback(() => {
    let isLoggingOut = false;
    updateModalStates({
      show: true,
      title: 'Log Out',
      size: 'sm',
      showCloseButton: false,
      static: true,
      children: (
        <ActionConfirmation
          action="log out"
          type='warning'
          loading={isLoggingOut}
          confirmationMessage="Are you sure you want to log out of your account?"
          onClose={() => {
            if (!isLoggingOut) closeModal();
          }}
          onConfirm={async () => {
            isLoggingOut = true;
            try {
              await signOut();
              closeModal();
            } catch (error) {
              closeModal();
            } finally {
              isLoggingOut = false;
            }
          }}
        />
      )
    });
  }, [signOut, updateModalStates, closeModal]);

  return (
    <>
      <div className={`${showSidebar ? "flex flex-col flex-nowrap" : " hidden"} xl:flex xl:flex-col bg-white dark:bg-darkBg border-r border-gray-200 dark:border-darkBg-border h-screen ${isSidebarExpanded ? "!w-[300px]" : ""}`}>
        <SidebarHeader onMenuClick={menuClickHandler} isSidebarExpanded={isSidebarExpanded} />
        
        <CompanyInfo isSidebarExpanded={isSidebarExpanded} />
        <UserInfo isSidebarExpanded={isSidebarExpanded} />
        
        <AddPolicyButton isSidebarExpanded={isSidebarExpanded} />
        
        <Navigation
          isSidebarExpanded={isSidebarExpanded}
          onMenuClick={menuClickHandler}
          expandedCategories={expandedCategories}
          toggleCategory={toggleCategory}
          expandedNavItems={expandedNavItems}
          toggleNavItem={toggleNavItem}
        />
        
        <SidebarFooter
          isSidebarExpanded={isSidebarExpanded}
          onLogout={logout}
          onToggleSidebar={toggleSidebarExpansion}
        />
      </div>
      <ModalComponent />
    </>
  );
}

export default SidebarContent;
